import React, {ReactNode} from "react";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import MenuButton from "../../shared/menu/MenuButton";
import {Calendar} from "primereact/calendar";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import Administration from "../session/Administration";
import Downloader from "../../portal/document/Downloader";
import ContentMode from "../ContentMode";
import DateHelper from "../../shared/helper/DateHelper";
import {
	ConfigurationType,
	LogConfigurationMode,
	LogFileConfiguration,
	LogFileConfigurationInterface,
	SupportEntryGroup
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../shared/immutable/Immutable";
import LengthUnit from "../../shared/units/LengthUnit";
import DomHelper from "../../shared/helper/DomHelper";
import Translate from "../../shared/translation/Translate";
import Helper from "../../shared/components/Helper";
import {MessageType} from "../../shared/log/MessageInterface";
import Icon from "../../shared/components/Icon";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../shared/components/Info";
import {InputText} from "primereact/inputtext";
import EscapeHelper from "../../shared/helper/EscapeHelper";

export enum LoggingLevel {
	ALL = "ALL",
	DEBUG = "DEBUG",
	INFO = "INFO",
	WARN = "WARN",
	ERROR = "ERROR"
}

export interface LogsPanelProperties extends AbstractPanelProperties {
}

export interface LogsPanelState extends AbstractPanelState {
	logDate: Date;
	parameter?: Immutable<LogFileConfigurationInterface>;
	logIndex: number;
	logging: boolean;
	wrapLog: boolean;
	scrollLog: boolean;
	timer: number;
	logLevel: LoggingLevel;
	rawLog: string;
	logEntries: Array<string>;
	loadingOffset: number,
	loadedItemsFrom: number,
	loadedItemsTo: number,
	logListHeight: number;
	search: string;
	searchPos: number;
}

export class LogsPanelDefinition extends AbstractPanel<LogsPanelProperties, LogsPanelState> {
	private readonly timeout: number = 5;
	private readonly fetchLimit: number = 2 * 1024 * 1024; // 2MB
	private readonly lineRegexp: RegExp =
		/^([0-9]{4}-[0-9]{2}-[0-9]{2}) ([0-9]{2}:[0-9]{2}:[0-9]{2},[0-9]{3}) (INFO|DEBUG|WARN|ERROR) /;
	private readonly logLevelRanking: Map<LoggingLevel, number> = new Map<LoggingLevel, number>([
		[LoggingLevel.ALL, 5],
		[LoggingLevel.DEBUG, 4],
		[LoggingLevel.INFO, 3],
		[LoggingLevel.WARN, 2],
		[LoggingLevel.ERROR, 1]
	])
	private refreshInterval: number;
	private readonly escapeMap: Map<string, string>;
	private logListElement?: HTMLDivElement;
	private resizeObserver: ResizeObserver;

	constructor(props: Readonly<LogsPanelProperties>) {
		super(props);

		this.state = {
			...super.state,
			logDate: DateHelper.currentDate(),
			logIndex: 0,
			logging: false,
			wrapLog: false,
			scrollLog: true,
			timer: this.timeout,
			logLevel: LoggingLevel.ALL,
			rawLog: "",
			logEntries: [],
			logListHeight: 0,
			search: "",
			searchPos: 0
		};

		this.downloadLog = this.downloadLog.bind(this);
		this.updateLog = this.updateLog.bind(this);
		this.getServerLogs = this.getServerLogs.bind(this);
		this.computeViewport = this.computeViewport.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.search = this.search.bind(this);
		this.testLineheight = this.testLineheight.bind(this);

		this.escapeMap = new Map<string, string>([
			['&', '&amp;'],
			['<', '&lt;'],
			['>', '&gt;'],
			['"', '&quot;'],
			['\'', '&#39;']
		]);
		this.refreshInterval = window.setInterval(this.updateLog, 1000);
		this.resizeObserver = new ResizeObserver(() => {
			this.computeViewport()
		});
	}

	private updateParameter() {
		this.setState({
			parameter: this.props.logConfig
		});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		this.updateParameter();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		if (FunctionHelper.exists(this.logListElement)) {
			this.resizeObserver.unobserve(this.logListElement!);
		}
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<LogsPanelProperties>, prevState: Readonly<LogsPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode || !prevProps.logConfig?.equals(this.props.logConfig)) {
			this.updateParameter();
		}
	}

	private search(reverse: boolean): void {
		if (FunctionHelper.isEmpty(this.state.search) || !FunctionHelper.exists(this.logListElement)) {
			return;
		}

		let found: boolean = false;
		let startPos: number = this.state.searchPos;
		let searchPos: number = reverse ? 0 : Number.POSITIVE_INFINITY;
		let entryCount = 0;
		for (let entry of this.state.logEntries) {
			entryCount++;

			let escapedQuery: string = EscapeHelper.escapeRegex(this.state.search);
			let searchRegex: RegExp = new RegExp(
				"<.+?" + escapedQuery + ".+?>|" + escapedQuery, "gi"
			);

			if (entry.match(searchRegex)) {
				let loggingLevel: LoggingLevel = this.getLoggingLevel(entry);
				if (FunctionHelper.applyValue(
					this.logLevelRanking.get(loggingLevel), 0
				) > (FunctionHelper.applyValue(
					this.logLevelRanking.get(this.state.logLevel), 0
				))) {
					continue;
				}

				found = true;

				if ((reverse && entryCount < startPos && entryCount > searchPos) ||
					(!reverse && entryCount > startPos && entryCount < searchPos)) {
					searchPos = entryCount;
				}
			}
		}

		if (searchPos > 0 && searchPos < Number.POSITIVE_INFINITY) {
			let logListRect: DOMRect = this.logListElement!.getBoundingClientRect();
			let scrollpos: number = 0;

			let entryCount = 0;
			for (let entry of this.state.logEntries) {
				if (entryCount === searchPos) {
					break;
				}

				entryCount++;
				scrollpos += this.testLineheight(entry);
			}

			if (scrollpos < logListRect.height / 2) {
				scrollpos = 0;
			} else {
				scrollpos -= logListRect.height / 2;
			}

			this.logListElement!.scrollTop = scrollpos;
		} else {
			searchPos = reverse ? Number.POSITIVE_INFINITY : 0;

			if (found) {
				this.setState({
					searchPos: searchPos
				}, () => {
					this.search(reverse);
				});
				return;
			}
		}

		this.setState({
			searchPos: searchPos
		});
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter") {
			this.search(false);
		}
	}

	private async downloadLog(): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);
		let dateString: string = DateHelper.buildDateString(this.state.logDate);

		try {
			let data: Buffer = await administration.getSupport(
				[SupportEntryGroup.Logs], dateString, dateString
			);
			await downloader.download(new Blob([data]), "report-" + dateString + ".zip");

			this.setState({
				exception: undefined
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.downloadLog();
			}
		}
	}

	private async updateLog(): Promise<void> {
		if (this.props.contentMode !== ContentMode.LOGS || !this.state.logging) {
			return;
		}

		if (this.state.timer === 0) {
			this.setState({
				timer: this.timeout
			});
		} else {
			this.setState({
				timer: this.state.timer - 1
			});

			return;
		}

		await this.getServerLogs();
	}

	private processLog(logs?: string): Array<string> {
		if (!FunctionHelper.exists(logs)) {
			return [];
		}

		// escape tags
		logs!.replace(/[&<>"']/gm, (char: string) => {
			return FunctionHelper.applyValue(this.escapeMap.get(char), char);
		});

		let logEntries: Array<string> = [];
		let tempLines: Array<string> = logs!.split('\n');
		let logEntry: string | undefined;

		for (let line of tempLines) {
			let isEntry: boolean = false;

			// remove empty lines
			if (line.trim() === "") {
				continue;
			}

			// check if line is new entry
			if (this.lineRegexp.test(line)) {
				isEntry = true;
			}

			// if log entry is unset this is the first line
			if (!FunctionHelper.exists(logEntry)) {
				logEntry = line;
				continue;
			}

			// if line is no new entry, add the line to the current log entry
			if (!isEntry) {
				logEntry += "\n" + line;
				continue;
			}

			logEntries.push(logEntry!);
			logEntry = line;
		}

		if (FunctionHelper.exists(logEntry)) {
			logEntries.push(logEntry!);
		}

		return logEntries;
	}

	private async getServerLogs(): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let logs: string;

		let logDate: string | undefined;
		if (this.state.logDate.toDateString() !== DateHelper.currentDate().toDateString()) {
			logDate = DateHelper.buildDateString(this.state.logDate);
		}

		try {
			let logLength = await administration.getLogLength(logDate);

			// apply fetch limit
			logLength = Math.min(logLength, this.state.logIndex + this.fetchLimit);

			// stop when log length is reached
			if (this.state.logIndex + 1 >= logLength) {
				return;
			}

			logs = await administration.getLog(this.state.logIndex + "-" + logLength, logDate);

			this.setState({
				exception: undefined,
				logIndex: logLength
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.getServerLogs();
			}
			return;
		}


		let rawLog: string = "";

		if (FunctionHelper.exists(this.state.rawLog)) {
			rawLog += this.state.rawLog;
		}

		rawLog += logs;

		this.setState({
			rawLog: rawLog,
			logEntries: this.processLog(rawLog)
		}, () => this.computeViewport(this.state.scrollLog));
	}

	private computeViewport(scrollLog: boolean = false): void {
		if (!FunctionHelper.exists(this.logListElement)) {
			return;
		}

		let logListRect: DOMRect = this.logListElement!.getBoundingClientRect();
		let logListHeight: number = logListRect.height - DomHelper.SCROLLBAR_SIZE;
		let scrollOffset: number = this.logListElement!.scrollTop;

		let listHeight: number = 0;
		let fromSet: boolean = false;
		let loadedItemsFrom: number = 1;
		let toSet: boolean = false;
		let loadedItemsTo: number = 1;
		let loadingOffset: number = 0;
		let viewportOffset: number = 0;

		for (let entry of this.state.logEntries) {
			let loggingLevel: LoggingLevel = this.getLoggingLevel(entry);
			let isFiltered: boolean = false;

			if (FunctionHelper.applyValue(
				this.logLevelRanking.get(loggingLevel), 0
			) > FunctionHelper.applyValue(
				this.logLevelRanking.get(this.state.logLevel), 0
			)) {
				isFiltered = true;
			}

			let itemHeight: number = this.testLineheight(entry);
			if (!fromSet && loadingOffset + itemHeight < scrollOffset) {
				if (!isFiltered) {
					loadingOffset += itemHeight;
				}

				loadedItemsFrom++;
				loadedItemsTo++;
			} else {
				fromSet = true;

				if (!toSet && fromSet && viewportOffset + loadingOffset < scrollOffset + logListHeight) {
					if (!isFiltered) {
						viewportOffset += itemHeight;
					}

					loadedItemsTo++;
				} else {
					toSet = true;
				}
			}

			if (!isFiltered) {
				listHeight += itemHeight;
			}
		}

		loadedItemsFrom = Math.max(loadedItemsFrom, 1);
		loadedItemsTo = Math.min(loadedItemsTo, this.state.logEntries.length);

		this.setState({
			loadingOffset: loadingOffset,
			loadedItemsFrom: loadedItemsFrom,
			loadedItemsTo: loadedItemsTo,
			logListHeight: listHeight
		}, () => {
			if (scrollLog && FunctionHelper.exists(this.logListElement)) {
				this.logListElement!.scrollTop = this.logListElement!.scrollHeight;
			}
		});
	}

	private testLineheight(line: string): number {
		let lineHeight = 20;
		let sublines: Array<string> = line.split('\n');
		let lineCount = Math.max(sublines.length, 1);
		return lineCount * lineHeight;
	}

	private getLoggingLevel(entry: string): LoggingLevel {
		let loggingLevel: LoggingLevel = LoggingLevel.ALL;
		let loggingLevelIndex: number | undefined;

		for (let level of Object.values(LoggingLevel)) {
			let index: number = entry.indexOf(level);

			if (index !== -1 && (!FunctionHelper.exists(loggingLevelIndex) || index < loggingLevelIndex!)) {
				loggingLevelIndex = index;
				loggingLevel = level;
			}
		}

		return loggingLevel;
	}

	private async updateDebugMode(): Promise<void> {
		let logConfig: LogFileConfiguration | undefined = LogFileConfiguration.fromJson(this.state.parameter?.get());
		let administration: Administration = this.props.context.session.getAdministration();

		try {
			await administration.updateConfiguration(ConfigurationType.Log, logConfig);

			this.setState({
				exception: undefined
			})
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.updateDebugMode();
			}
			return;
		}
	}

	private renderLogs(): ReactNode {
		let logs: Array<ReactNode> = [];
		let entryCount: number = 0;

		for (let entry of this.state.logEntries) {
			entryCount++;

			if (entryCount < this.state.loadedItemsFrom || entryCount > this.state.loadedItemsTo) {
				continue;
			}

			let loggingLevel: LoggingLevel = this.getLoggingLevel(entry);
			if (FunctionHelper.applyValue(
				this.logLevelRanking.get(loggingLevel), 0
			) > (FunctionHelper.applyValue(
				this.logLevelRanking.get(this.state.logLevel), 0
			))) {
				continue;
			}

			let content: string = entry;

			if (!FunctionHelper.isEmpty(this.state.search)) {
				let escapedQuery: string = EscapeHelper.escapeRegex(this.state.search);
				let searchRegex: RegExp = new RegExp(
					"<.+?" + escapedQuery + ".+?>|" + escapedQuery, "gi"
				);

				let classList: Array<string> = ["result"];
				if (entryCount === this.state.searchPos) {
					classList.push("active");
				}

				content = content.replaceAll(searchRegex, (match) => {
					return '<span class="' + classList.join(" ") + '">' + match + '</span>';
				});
			}

			content = content.replace(loggingLevel, '<span class="tag">' + loggingLevel + '</span>');

			logs.push(
				<li className={loggingLevel.toLowerCase() + "-line"} key={logs.length}>
					<span className="line-number">{entryCount}</span>
					<pre dangerouslySetInnerHTML={{__html: content}}/>
				</li>
			);
		}

		return (
			<ul style={{top: this.state.loadingOffset + LengthUnit.PIXEL}}>
				{logs}
			</ul>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let logConfig: LogFileConfiguration | undefined = LogFileConfiguration.fromJson(this.state.parameter?.get());

		if (!FunctionHelper.exists(logConfig)) {
			return null;
		}

		let currentDate: Date = DateHelper.currentDate();
		let isCurrent: boolean = this.state.logDate.getTime() === currentDate.getTime();

		return (
			<div className="screen admin-logs-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="body">
					<h1>
						<Translate {...this.props} data="adminscreen.panels.logs.title" namespace="screen"/>
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.logs.general"} namespace={"help"}
								   markdown={true}/>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-play"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.play"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-pause"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.pause"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-rewind"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.rewind"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-delete small"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.delete"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-reload"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.reload"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-list bottom"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.end"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-list wrap"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.break"} namespace={"help"}/>
						</p>
						<p style={{display: "flex"}}>
							<Icon icon={"icon-download small"} style={{marginRight: "10px"}}/>
							<Translate {...this.props} data={"global.screen.admin.logs.download"} namespace={"help"}/>
						</p>
						<Translate {...this.props} data={"global.screen.admin.logs.fields"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"global.screen.admin.logs.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						{this.renderError()}
					</fieldset>
					<div className="functions">
						{!this.state.logging ?
							<MenuButton {...this.props} icon="icon icon-play" disabled={!isCurrent}
										tooltip={Translate.value(
											"adminscreen.panels.logs.start", {ns: "screen"}
										)}
										onClick={() => this.setState({
											logging: true,
											timer: 0
										})}/> :
							<MenuButton {...this.props} icon="icon icon-pause" disabled={!isCurrent}
										tooltip={Translate.value(
											"adminscreen.panels.logs.stop", {ns: "screen"}
										)}
										onClick={() => this.setState({
											timer: this.timeout,
											logging: false
										})}/>
						}
						<MenuButton {...this.props} icon="icon icon-rewind" disabled={!isCurrent}
									tooltip={Translate.value(
										"adminscreen.panels.logs.reset", {ns: "screen"}
									)}
									onClick={() => this.setState({
										rawLog: "",
										logEntries: [],
										logIndex: 0,
										logListHeight: 0,
										timer: 0
									})}/>
						<MenuButton {...this.props} icon="icon icon-delete small" disabled={!isCurrent}
									tooltip={Translate.value(
										"adminscreen.panels.logs.clear", {ns: "screen"}
									)}
									onClick={() => this.setState({
										logEntries: [],
										logListHeight: 0
									})}/>
						<MenuButton {...this.props} icon="icon icon-reload" className="button-refresh"
									label={this.state.logging ? this.state.timer.toString() : ""} disabled={!isCurrent}
									tooltip={Translate.value(
										"adminscreen.panels.logs.reload", {ns: "screen"}
									)}
									onClick={() => this.setState({
										timer: 0
									})}/>
						<MenuButton {...this.props} icon="icon icon-list bottom"
									className={this.state.scrollLog ? "active" : ""}
									tooltip={Translate.value(
										"adminscreen.panels.logs.align_bottom", {ns: "screen"}
									)}
									onClick={() =>
										this.setState({
											scrollLog: !this.state.scrollLog
										}, () => {
											if (this.state.scrollLog && FunctionHelper.exists(this.logListElement)) {
												this.logListElement!.scrollTop = this.logListElement!.scrollHeight;
											}
										})
									}/>
						<MenuButton {...this.props} icon="icon icon-list wrap"
									className={this.state.wrapLog ? "active" : ""}
									tooltip={Translate.value(
										"adminscreen.panels.logs.wrap", {ns: "screen"}
									)}
									onClick={() => this.setState({
										wrapLog: !this.state.wrapLog
									})}/>
						<div className="input-group">
							<div className="p-inputgroup">
								<Calendar value={this.state.logDate} dateFormat="dd.mm.yy" maxDate={currentDate}
										  onChange={(event) => {
											  let isCurrent: boolean =
												  (event.value as Date).getTime() === currentDate.getTime();

											  this.setState({
												  logIndex: 0,
												  rawLog: "",
												  logEntries: [],
												  logDate: event.value as Date,
												  logging: isCurrent,
												  timer: 0
											  }, async () => {
												  if (!isCurrent) {
													  await this.getServerLogs();
												  }
											  });
										  }}
										  locale={this.props.language}/>
								<Button icon="icon icon-calendar"
										tooltip={Translate.value(
											"adminscreen.panels.logs.today", {ns: "screen"}
										)}
										onClick={() => this.setState({
											logIndex: 0,
											rawLog: "",
											logEntries: [],
											logDate: currentDate,
											logging: true,
											timer: 0
										})}/>
							</div>
						</div>
						<MenuButton {...this.props} icon="icon icon-download small" onClick={this.downloadLog}
									tooltip={Translate.value(
										"adminscreen.panels.logs.download", {ns: "screen"}
									)}/>
						<div className="input-group">
							<div className="p-inputgroup">
								<MenuButton {...this.props} icon="icon icon-close" className="p-inputgroup-addon"
											onClick={() => this.setState({
												search: "",
												searchPos: 0
											})}
											tooltip={Translate.value(
												"adminscreen.panels.logs.search.clear", {ns: "screen"}
											)}/>
								<InputText id="control-search" value={this.state.search}
										   placeholder={Translate.value(
											   "adminscreen.panels.logs.search.label", {ns: "screen"}
										   )}
										   onInput={(e) => this.setState({
											   search: e.currentTarget.value,
											   searchPos: 0
										   })}/>
								<MenuButton {...this.props} icon="icon icon-down" className="p-inputgroup-addon"
											onClick={() => this.search(false)}
											tooltip={Translate.value(
												"adminscreen.panels.logs.search.next", {ns: "screen"}
											)}/>
								<MenuButton {...this.props} icon="icon icon-up" className="p-inputgroup-addon"
											onClick={() => this.search(true)}
											tooltip={Translate.value(
												"adminscreen.panels.logs.search.previous", {ns: "screen"}
											)}/>
							</div>
						</div>
						<div className="input-group">
							<label htmlFor="control-log-mode">
								<Translate {...this.props} data="adminscreen.panels.logs.mode.label"
										   namespace="screen"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={LogFileConfiguration.getDebugModeDescription()}/>
							</label>
							<Dropdown inputId="control-log-mode"
									  value={FunctionHelper.applyValue(
										  logConfig.debugMode, LogFileConfiguration.getDebugModeDefault
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.mode.none", {ns: "screen"}
											  ),
											  value: LogConfigurationMode.None
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.mode.debug", {ns: "screen"}
											  ),
											  value: LogConfigurationMode.Debug
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.mode.request", {ns: "screen"}
											  ),
											  value: LogConfigurationMode.DebugRequest
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.mode.payload", {ns: "screen"}
											  ),
											  value: LogConfigurationMode.DebugRequestPayload
										  }
									  ]}
									  onChange={(e) => {
										  logConfig!.debugMode = e.value;

										  this.setState({
											  parameter: Immutable.from(
												  logConfig?.toJson() as LogFileConfigurationInterface
											  )
										  }, this.updateDebugMode);
									  }}
									  appendTo={document.getElementById("root")}/>
						</div>
						<div className="input-group">
							<label htmlFor="control-log-mode">
								<Translate {...this.props} data="adminscreen.panels.logs.level.label"
										   namespace="screen"/>
							</label>
							<Dropdown value={this.state.logLevel} inputId="control-log-level"
									  options={[
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.level.all", {ns: "screen"}
											  ),
											  value: LoggingLevel.ALL
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.level.debug", {ns: "screen"}
											  ),
											  value: LoggingLevel.DEBUG
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.level.info", {ns: "screen"}
											  ),
											  value: LoggingLevel.INFO
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.level.warning", {ns: "screen"}
											  ),
											  value: LoggingLevel.WARN
										  },
										  {
											  label: Translate.value(
												  "adminscreen.panels.logs.level.error", {ns: "screen"}
											  ),
											  value: LoggingLevel.ERROR
										  }
									  ]}
									  onChange={(e) => this.setState({
										  logLevel: e.value
									  }, () => this.computeViewport())}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div ref={(ref) => {
						if (!FunctionHelper.exists(ref)) {
							return;
						}

						this.logListElement = ref!;
						this.resizeObserver.observe(this.logListElement);
					}} className={
						"logs " + this.state.logLevel.toLowerCase() + "-level" + (this.state.wrapLog ? " wrap" : "")
					} onScroll={() => this.computeViewport()}>
						<div className="loading-spacer" style={this.state.logListHeight > 0 ?
							{height: this.state.logListHeight + LengthUnit.PIXEL} : undefined
						}/>
						{this.renderLogs()}
					</div>
				</div>
			</div>
		);
	}
}

const LogsPanel = React.memo(LogsPanelDefinition,
	(prevProps: Readonly<LogsPanelProperties>, nextProps: Readonly<LogsPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (prevProps.logConfig !== nextProps.logConfig) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default LogsPanel;